import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import useBmoStore from 'common/stores/bmoStore';
import {Layout, Button, Text} from '@phonectas/unifon-ui-kit';
import {fileSizeToMB, formatFileSize, MAX_FILE_SIZE} from 'utils/file';

// Backend accepts max 5 files per call.
const MAX_FILES = 5;

interface SimpleUploaderProps {
    id: string;
    onUploadOk?: boolean;
}

const SimpleUploader: React.FC<SimpleUploaderProps> = ({id, onUploadOk}) => {
	const [t] = useTranslation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [error, setError] = useState('');
	const postAttachments = useBmoStore((state) => state.postAttachments);
	const getTicketId = useBmoStore((state) => state.getTicketId);
	const allowedExtensions = useMemo(() => ['.jpeg', '.jpg', '.png'], []);

	const handleAddFiles = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newFiles = event.target.files ? Array.from(event.target.files) : [];
			// Reset so picking the same file again still fires onChange.
			event.target.value = '';
			const tooLarge = newFiles.find((file) => file.size > MAX_FILE_SIZE);
			if (tooLarge) {
				setError(t('File {{name}} is too large ({{size}} MB). Maximum allowed size is {{max}}', {
					name: tooLarge.name,
					size: fileSizeToMB(tooLarge.size),
					max: formatFileSize(MAX_FILE_SIZE),
				}));
				return;
			}
			const invalidType = newFiles.find((file) =>
				!allowedExtensions.includes(file.name.substring(file.name.lastIndexOf('.')).toLowerCase()));
			if (invalidType) {
				setError(t('Invalid file type: {{name}}', {name: invalidType.name}));
				return;
			}
			const names = new Set(files.map((file) => file.name));
			const merged = [...files, ...newFiles.filter((file) => !names.has(file.name))];
			if (merged.length > MAX_FILES) {
				setError(t('Max {{max}} files at once', {max: MAX_FILES}));
				return;
			}
			setError('');
			setFiles(merged);
		},
		[allowedExtensions, files, t]
	);

	const handleClear = useCallback(() => {
		setFiles([]);
		setError('');
	}, []);

	const handleButtonClick = useCallback(() => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	}, []);

	const onUploadFiles = useCallback(async () => {
		try {
			const formData = new FormData();
			files.forEach((file) => {
				formData.append('files', file);
			});
			await postAttachments(id, formData, () => {
				setFiles([]);
				if (onUploadOk) {
					getTicketId(id);
				}
			});
		} catch (error) {
			console.error(error);
		}
	}, [files, id, postAttachments, onUploadOk, getTicketId]);

	return (
		<Layout>
			<Button
				variant="primary"
				onClick={handleButtonClick}
				disabled={files.length >= MAX_FILES}
			>
				{t('Add files')}
			</Button>
			<Button
				variant="primary"
				color="success"
				disabled={files.length === 0}
				onClick={onUploadFiles}
			>
				{t('Upload files')}
			</Button>
			<Button
				variant="secondary"
				color="error"
				disabled={files.length === 0}
				onClick={handleClear}
			>
				{t('Clear')}
			</Button>
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleAddFiles}
				style={{display: 'none'}}
				accept={allowedExtensions.join(',')}
				multiple
			/>
			{error ? <Text color="error">{error}</Text> : null}
			<Layout fullWidth nowrap>
				{
					files.map((file, index) => (
						<p key={index}>{file.name}</p>
					))
				}
			</Layout>
		</Layout>
	);
}

export default SimpleUploader;
